/**
 * ============================================================================
 * OosInvestigation — Phase 7 frontend (OOS Module).
 * Owner: Phase 7 — Out-of-Specification Module.
 * Route: /oos/:id
 * Purpose: Phase 1 + Phase 2 investigation surface. Three regions:
 *   1. Lab result panel — read-only display of the original measurement.
 *      OOS-006: the lab result is APPEND-ONLY; the UI never exposes an edit
 *      affordance for it.
 *   2. Phase 1 tab — outcome dropdown (ASSIGNABLE_CAUSE_FOUND | NEGATIVE),
 *      summary textarea, conclude button. NEGATIVE auto-opens Phase 2 on
 *      the server (OOS-001 + OOS-009).
 *   3. Phase 2 tab — cause-classification dropdown
 *      (LABORATORY_ERROR | MANUFACTURING_ASSIGNABLE | INHERENT_VARIABILITY
 *       | OTHER), final-conclusion textarea, conclude button (opens CAPA;
 *      optionally a Deviation).
 *   4. Close form — captured at the bottom; OOS-007 is enforced server-side
 *      (UI also disables the submit until cause has been classified).
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md + tokens-v2):
 *   - Editorial layout, generous whitespace.
 *   - Phase tabs styled like the QualityMetricsDashboard view selector.
 *   - Active phase deduced from server status to drive default tab.
 *   - "OOS-006: lab result frozen" inline tag below the result panel.
 *
 * URS rows: OOS-001, OOS-002, OOS-005, OOS-006, OOS-007, OOS-009, CAPA-015.
 * ============================================================================ */

import { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../../api/client';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './OosInvestigation.module.css';
import { useOosDetailQuery } from './useOosDetailQuery';
import type { OosDetail } from './useOosDetailQuery';
import { CostAttributionPanel } from '../Finance/CostAttribution';
import { RelatedRecords } from '../../components/RelatedRecords';
import { OosDepthPanel } from './OosDepthPanel';
import { useDeviationsQuery } from '../DeviationTriage/useDeviationsQuery';
import { AiAdvisoryChip } from '../../components/AiAdvisoryChip';
import { AiSimilarRecord } from '../../components/AiCluster';

const PHASE1_OUTCOMES = ['ASSIGNABLE_CAUSE_FOUND', 'NEGATIVE'] as const;

const ASSIGNABLE_CAUSES = [
  'LABORATORY_ERROR',
  'MANUFACTURING_ASSIGNABLE',
  'INHERENT_VARIABILITY',
  'OTHER',
] as const;

type Tab = 'phase1' | 'phase2' | 'close';

export interface OosInvestigationProps {
  oosId: string;
}

export function OosInvestigation({ oosId }: OosInvestigationProps) {
  const { t, i18n } = useTranslation();
  const detailQ = useOosDetailQuery(oosId);
  const detail: OosDetail | undefined = detailQ.data;

  const [tab, setTab] = useState<Tab>('phase1');
  const [phase1Outcome, setPhase1Outcome] =
    useState<(typeof PHASE1_OUTCOMES)[number]>('ASSIGNABLE_CAUSE_FOUND');
  const [phase1Summary, setPhase1Summary] = useState('');
  const [cause, setCause] = useState<(typeof ASSIGNABLE_CAUSES)[number]>('LABORATORY_ERROR');
  const [finalConclusion, setFinalConclusion] = useState('');
  const [closureSignerUserId, setClosureSignerUserId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Auto-select the relevant tab based on server state.
  useEffect(() => {
    if (!detail) return;
    if (detail.status === 'PHASE_1') setTab('phase1');
    else if (detail.status === 'PHASE_2') setTab('phase2');
    else if (detail.status === 'CONCLUDED') setTab('close');
  }, [detail?.status]);

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  // AI-204 — pull recent deviations as the candidate corpus for OOS↔DEV linking.
  // This hook MUST run before the early returns below: calling it after a
  // conditional return changes the hook count between renders and throws React
  // #310 ("rendered more hooks than during the previous render") the moment the
  // detail query resolves and the component renders past the loading/error guards.
  const candidateDeviationsQ = useDeviationsQuery({});
  const candidateDeviations = candidateDeviationsQ.data?.items ?? [];

  async function submitPhase1() {
    if (!detail) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const resp = await fetch(apiUrl(`/api/v1/oos/${encodeURIComponent(detail.id)}/phase1`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ outcome: phase1Outcome, summary: phase1Summary }),
      });
      if (!resp.ok) {
        setErrorMessage(`Phase 1 conclude failed (HTTP ${resp.status}).`);
      } else {
        await detailQ.refetch?.();
        setPhase1Summary('');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function submitPhase2() {
    if (!detail) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const resp = await fetch(apiUrl(`/api/v1/oos/${encodeURIComponent(detail.id)}/phase2`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          assignableCauseKind: cause,
          finalConclusion,
        }),
      });
      if (!resp.ok) {
        setErrorMessage(`Phase 2 conclude failed (HTTP ${resp.status}).`);
      } else {
        await detailQ.refetch?.();
        setFinalConclusion('');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function submitClose() {
    if (!detail) return;
    if (!closureSignerUserId) {
      setErrorMessage('A closure signer user id is required.');
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const resp = await fetch(apiUrl(`/api/v1/oos/${encodeURIComponent(detail.id)}/close`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ closureSignerUserId }),
      });
      if (!resp.ok) {
        setErrorMessage(`Close failed (HTTP ${resp.status}). OOS-007: assignable cause required.`);
      } else {
        await detailQ.refetch?.();
        setClosureSignerUserId('');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (detailQ.isLoading || !detail) {
    return (
      <div className={wstyles.adminScreen}>
        <div className={styles.skeletonRow} aria-hidden />
        <div className={styles.skeletonRow} aria-hidden />
        <div className={styles.skeletonRow} aria-hidden />
      </div>
    );
  }

  if (detailQ.isError) {
    return (
      <div className={wstyles.adminScreen}>
        <div className={styles.errorBanner} role="alert">
          {t('oos.investigation.error', {
            defaultValue: 'Could not load the OOS event.',
          })}
        </div>
      </div>
    );
  }

  const phase1Enabled = detail.status === 'PHASE_1';
  const phase2Enabled = detail.status === 'PHASE_2';
  const closeEnabled =
    detail.status === 'CONCLUDED' && detail.assignableCauseKind != null;

  // AI-202 — investigation guide prompt grounded in current OOS state.
  const aiGuidePrompt =
    `OOS investigation context. Number: ${detail.oosNumber}. Status: ${detail.status}. ` +
    `Sample: ${detail.sampleRef}. Batch: ${detail.batchRef ?? 'n/a'}. ` +
    `Method: ${detail.testMethodRef}. Spec: ${detail.specLowerBound ?? '-inf'}..${detail.specUpperBound ?? '+inf'} ${detail.specUnits}. ` +
    `Observed: ${detail.resultValue} ${detail.resultUnits}. Kind: ${detail.outOfSpecKind}. ` +
    `Per FDA OOS Guidance, suggest the NEXT 3 investigative steps for Phase 1 (laboratory review) and ` +
    `the NEXT 2 likely Phase 2 cause-classification candidates. Return JSON {summary, concerns:[]}.`;

  // AI-204 — query text built from the OOS for similarity matching.
  const aiLinkerQuery =
    `${detail.testMethodRef} — sample ${detail.sampleRef} — batch ${detail.batchRef ?? 'n/a'} — ` +
    `${detail.outOfSpecKind} (result ${detail.resultValue} ${detail.resultUnits} vs spec ` +
    `${detail.specLowerBound ?? '-∞'}..${detail.specUpperBound ?? '+∞'})`;

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('oos.investigation.eyebrow', { defaultValue: 'Quality Events' })}
        title={
          <>
            {t('oos.investigation.title', { defaultValue: 'OOS investigation' })}{' '}
            <span className={styles.mono}>{detail.oosNumber}</span>
          </>
        }
        subtitle={t('oos.investigation.subtitle', {
          defaultValue:
            'FDA OOS Guidance Phase 1 / Phase 2 workflow. Lab data is append-only (OOS-006).',
        })}
        actions={
          <span className={`${styles.chip} ${statusChipClass(detail.status)}`}>
            {detail.status}
          </span>
        }
      />

      {/* AI-202 + AI-204 — investigation guide chip + OOS↔DEV linker */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 12, margin: '12px 0' }}>
        <AiAdvisoryChip
          surface="oos.guide"
          prompt={aiGuidePrompt}
          cacheKey={`oos-guide:${detail.oosNumber}:${detail.status}:${detail.resultValue}`}
          jsonSchema={{
            type: 'object',
            properties: {
              summary:  { type: 'string' },
              concerns: { type: 'array', items: { type: 'string' } },
            },
            required: ['summary'],
          }}
          citations={detail.batchRef ? [{ kind: 'batch', id: detail.batchRef, label: detail.batchRef }] : []}
        />
        <AiSimilarRecord
          queryText={aiLinkerQuery}
          entityKind="deviation"
          title={t('oos.investigation.aiLinkerTitle', { defaultValue: 'Possible linked deviations' })}
          candidates={candidateDeviations.slice(0, 30).map((d) => ({
            id: d.deviationId,
            text: `${d.title} (${d.classification} · ${d.sourceArea ?? 'no-area'})`,
            label: d.deviationId,
            href: `/deviations/${encodeURIComponent(d.deviationId)}`,
          }))}
          limit={3}
          minSimilarity={0.5}
        />
      </div>

      {/* Lab result panel — read-only, OOS-006 frozen. */}
      <Card>
        <CardHeader>
          <h2 className={wstyles.sectionTitle}>
            {t('oos.investigation.lab.title', { defaultValue: 'Lab measurement' })}
          </h2>
        </CardHeader>
        <CardBody>
          <dl className={styles.dl}>
            <div className={styles.dlRow}>
              <dt>Sample ref</dt>
              <dd className={styles.mono}>{detail.sampleRef}</dd>
            </div>
            <div className={styles.dlRow}>
              <dt>Batch ref</dt>
              <dd className={styles.mono}>{detail.batchRef ?? '—'}</dd>
            </div>
            <div className={styles.dlRow}>
              <dt>Test method</dt>
              <dd className={styles.mono}>{detail.testMethodRef}</dd>
            </div>
            <div className={styles.dlRow}>
              <dt>Spec</dt>
              <dd className={styles.mono}>
                {detail.specLowerBound ?? '−∞'} … {detail.specUpperBound ?? '+∞'}{' '}
                {detail.specUnits}
              </dd>
            </div>
            <div className={styles.dlRow}>
              <dt>Result</dt>
              <dd className={styles.resultBig}>
                <span className={styles.resultValueBig}>{detail.resultValue}</span>{' '}
                <span className={styles.resultUnits}>{detail.resultUnits}</span>
              </dd>
            </div>
            <div className={styles.dlRow}>
              <dt>Kind</dt>
              <dd>
                <span className={`${styles.chip} ${kindChipClass(detail.outOfSpecKind)}`}>
                  {detail.outOfSpecKind.replaceAll('_', ' ')}
                </span>
              </dd>
            </div>
            <div className={styles.dlRow}>
              <dt>Reported</dt>
              <dd>{dateFmt.format(new Date(detail.reportedAtUtc))}</dd>
            </div>
          </dl>
          <p className={styles.frozenTag}>
            OOS-006 — lab measurement is append-only post-INSERT; this panel is
            read-only by design.
          </p>
        </CardBody>
      </Card>

      {/* Tabs. */}
      <div className={styles.tabBar} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'phase1'}
          className={`${styles.tabBtn} ${tab === 'phase1' ? styles.tabBtnActive : ''}`}
          onClick={() => setTab('phase1')}
        >
          {t('oos.investigation.tabs.phase1', { defaultValue: 'Phase 1 — Lab' })}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'phase2'}
          className={`${styles.tabBtn} ${tab === 'phase2' ? styles.tabBtnActive : ''}`}
          onClick={() => setTab('phase2')}
        >
          {t('oos.investigation.tabs.phase2', { defaultValue: 'Phase 2 — Full' })}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'close'}
          className={`${styles.tabBtn} ${tab === 'close' ? styles.tabBtnActive : ''}`}
          onClick={() => setTab('close')}
        >
          {t('oos.investigation.tabs.close', { defaultValue: 'Close' })}
        </button>
      </div>

      {errorMessage && (
        <div className={styles.errorBanner} role="alert">
          {errorMessage}
        </div>
      )}

      {tab === 'phase1' && (
        <Card>
          <CardHeader>
            <h2 className={wstyles.sectionTitle}>
              {t('oos.investigation.phase1.title', {
                defaultValue: 'Phase 1 — Laboratory investigation',
              })}
            </h2>
          </CardHeader>
          <CardBody>
            <p className={styles.muted}>
              {t('oos.investigation.phase1.help', {
                defaultValue:
                  'Investigate instrument, calculation, and analyst technique. Selecting NEGATIVE auto-opens Phase 2 (OOS-001).',
              })}
            </p>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Outcome</span>
              <select
                className={styles.fieldSelect}
                value={phase1Outcome}
                onChange={(e) =>
                  setPhase1Outcome(e.target.value as (typeof PHASE1_OUTCOMES)[number])
                }
                disabled={!phase1Enabled}
              >
                {PHASE1_OUTCOMES.map((o) => (
                  <option key={o} value={o}>
                    {o.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Summary</span>
              <textarea
                className={styles.fieldText}
                rows={4}
                value={phase1Summary}
                disabled={!phase1Enabled}
                onChange={(e) => setPhase1Summary(e.target.value)}
                placeholder="Instrument calibration verified, no calculation error, …"
              />
            </label>
            <Button
              variant="primary"
              onClick={submitPhase1}
              disabled={!phase1Enabled || submitting}
            >
              {t('oos.investigation.phase1.submit', {
                defaultValue: 'Conclude Phase 1',
              })}
            </Button>
            {detail.phase1ConcludedUtc && (
              <p className={styles.muted}>
                Phase 1 concluded {dateFmt.format(new Date(detail.phase1ConcludedUtc))} —
                outcome <strong>{detail.phase1Outcome}</strong>.
              </p>
            )}
          </CardBody>
        </Card>
      )}

      {tab === 'phase2' && (
        <Card>
          <CardHeader>
            <h2 className={wstyles.sectionTitle}>
              {t('oos.investigation.phase2.title', {
                defaultValue: 'Phase 2 — Full investigation',
              })}
            </h2>
          </CardHeader>
          <CardBody>
            <p className={styles.muted}>
              {t('oos.investigation.phase2.help', {
                defaultValue:
                  'Classify the assignable cause. Manufacturing-assignable causes also open a Deviation (DEV-005).',
              })}
            </p>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Assignable cause</span>
              <select
                className={styles.fieldSelect}
                value={cause}
                onChange={(e) =>
                  setCause(e.target.value as (typeof ASSIGNABLE_CAUSES)[number])
                }
                disabled={!phase2Enabled}
              >
                {ASSIGNABLE_CAUSES.map((c) => (
                  <option key={c} value={c}>
                    {c.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Final conclusion</span>
              <textarea
                className={styles.fieldText}
                rows={5}
                value={finalConclusion}
                disabled={!phase2Enabled}
                onChange={(e) => setFinalConclusion(e.target.value)}
                placeholder="Root cause description, evidence, references…"
              />
            </label>
            <Button
              variant="primary"
              onClick={submitPhase2}
              disabled={!phase2Enabled || submitting}
            >
              {t('oos.investigation.phase2.submit', {
                defaultValue: 'Conclude Phase 2',
              })}
            </Button>
            {detail.phase2ConcludedUtc && (
              <p className={styles.muted}>
                Phase 2 concluded {dateFmt.format(new Date(detail.phase2ConcludedUtc))} —
                cause <strong>{detail.assignableCauseKind}</strong>.
              </p>
            )}
          </CardBody>
        </Card>
      )}

      {tab === 'close' && (
        <Card>
          <CardHeader>
            <h2 className={wstyles.sectionTitle}>
              {t('oos.investigation.close.title', { defaultValue: 'Close OOS' })}
            </h2>
          </CardHeader>
          <CardBody>
            <p className={styles.muted}>
              {t('oos.investigation.close.help', {
                defaultValue:
                  'OOS-007: an assignable_cause_kind must be set before close. The button is enabled only when the server reports CONCLUDED + cause non-null.',
              })}
            </p>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Closure signer (user id)</span>
              <input
                className={styles.fieldSelect}
                type="text"
                value={closureSignerUserId}
                onChange={(e) => setClosureSignerUserId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                disabled={!closeEnabled}
              />
            </label>
            <Button
              variant="primary"
              onClick={submitClose}
              disabled={!closeEnabled || submitting}
            >
              {t('oos.investigation.close.submit', { defaultValue: 'Close OOS' })}
            </Button>
            {detail.status === 'CLOSED' && (
              <p className={styles.muted}>
                Closed {detail.closedAtUtc && dateFmt.format(new Date(detail.closedAtUtc))} —
                signer <span className={styles.mono}>{detail.closureSignerUserId}</span>.
              </p>
            )}
          </CardBody>
        </Card>
      )}

      {/* History timeline. */}
      <Card>
        <CardHeader>
          <h2 className={wstyles.sectionTitle}>
            {t('oos.investigation.history.title', { defaultValue: 'Timeline' })}
          </h2>
        </CardHeader>
        <CardBody>
          <ol className={styles.timeline}>
            {detail.history.map((h) => (
              <li key={h.historyId} className={styles.timelineItem}>
                <span className={styles.timelineWhen}>
                  {dateFmt.format(new Date(h.eventAtUtc))}
                </span>
                <span className={styles.timelineWhat}>{h.eventCode}</span>
                {h.actorUserId && (
                  <span className={styles.timelineWho}>by {h.actorUserId.slice(0, 8)}…</span>
                )}
              </li>
            ))}
            {detail.history.length === 0 && (
              <li className={styles.muted}>No history yet.</li>
            )}
          </ol>
        </CardBody>
      </Card>

      {/* C-6 — FDA Phase-1 lab checklist + retest/resample tracking. */}
      <OosDepthPanel
        oosId={oosId}
        phase1Checklist={detail.phase1Checklist ?? []}
        retests={detail.retests ?? []}
        onChanged={() => { void detailQ.refetch?.(); }}
      />

      {/* C-4 — cross-module Related Records (event-linking engine). */}
      <RelatedRecords entityType="OOS" entityId={oosId} />

      {/* Finance module — Cost Attribution (v0.13 Tier 1.2). */}
      <CostAttributionPanel
        subjectEntityType="OOS"
        subjectEntityId={oosId}
        subjectDisplayLabel={`${detail.oosNumber} — ${detail.outOfSpecKind.replaceAll('_', ' ')}`}
      />
    </div>
  );
}

function statusChipClass(status: OosDetail['status']): string {
  switch (status) {
    case 'REPORTED':
      return styles.statusReported;
    case 'PHASE_1':
      return styles.statusPhase1;
    case 'PHASE_2':
      return styles.statusPhase2;
    case 'CONCLUDED':
      return styles.statusConcluded;
    case 'CLOSED':
      return styles.statusClosed;
    case 'CANCELLED':
      return styles.statusCancelled;
    default:
      return '';
  }
}

function kindChipClass(kind: OosDetail['outOfSpecKind']): string {
  switch (kind) {
    case 'OUT_OF_SPEC':
      return styles.kindOos;
    case 'OUT_OF_TREND':
      return styles.kindOot;
    case 'OUT_OF_EXPECTATION':
      return styles.kindOoe;
    default:
      return '';
  }
}
